/**
 * One retry after 2s on network failure, timeout (AbortError), or retryable HTTP (429, 5xx, 529).
 * Logs each failed attempt to the console before retrying.
 * Safe for Node (API routes) and browser.
 */
import { apiLogger } from "@/lib/logger/logger";

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function mergeAbortSignals(
  a: AbortSignal | null | undefined,
  timeoutMs: number,
): AbortSignal | undefined {
  const outer = a ?? undefined;
  if (timeoutMs <= 0) return outer;
  const t =
    typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
      ? AbortSignal.timeout(timeoutMs)
      : undefined;
  if (!t) return outer;
  if (!outer) return t;
  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any([outer, t]);
  }
  return t;
}

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init: RequestInit,
  options?: {
    retries?: number;
    delayMs?: number;
    /** Per-attempt request timeout (ms). Default 120s. */
    timeoutMs?: number;
    /** Log prefix */
    label?: string;
  },
): Promise<Response> {
  const retries = options?.retries ?? 1;
  const delayMs = options?.delayMs ?? 2000;
  const timeoutMs = options?.timeoutMs ?? 120_000;
  const label = options?.label ?? "anthropic-fetch";
  let lastErr: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await sleep(delayMs);
    }

    const signal = mergeAbortSignals(init.signal, timeoutMs);

    try {
      const res = await fetch(input, { ...init, ...(signal ? { signal } : {}) });
      if (res.ok) {
        apiLogger.debug({ route: label, attempt: attempt + 1, totalAttempts: retries + 1, status: res.status }, "Fetch attempt succeeded");
        return res;
      }
      const status = res.status;
      const retryable =
        status === 429 || status === 503 || status === 529 || (status >= 500 && status < 600);
      apiLogger.warn({ route: label, attempt: attempt + 1, totalAttempts: retries + 1, status, retryable }, "Fetch attempt returned non-OK status");
      if (!retryable || attempt === retries) return res;
    } catch (e) {
      lastErr = e;
      const name = e instanceof Error ? e.name : "";
      const msg = e instanceof Error ? e.message : String(e);
      const isAbort = name === "AbortError" || /aborted|timeout/i.test(msg);
      apiLogger.warn({ route: label, attempt: attempt + 1, totalAttempts: retries + 1, error: msg, kind: isAbort ? "timeout/abort" : "network" }, "Fetch attempt failed");
      if (attempt === retries) throw e instanceof Error ? e : new Error(String(e));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}
